import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL, getWsBaseUrl } from '../config';
import { useProject } from '../context/ProjectContext';
import {
  AgentCommandBlock,
  AgentCommandCard,
  applyCommandEvent,
  commandsFromAgentLogs,
} from './AgentCommandFeed';
import { openAgentVscode } from '../utils/openAgentVscode';

interface AgentSpec {
  id: string;
  name: string;
  description: string;
  capabilities: string[];
  modes: string[];
}

interface AgentRun {
  run_id: string;
  agent_id: string;
  ticket_key?: string | null;
  mode: string;
  status: string;
  status_detail?: string | null;
  progress?: Record<string, string> | null;
  branch_name?: string | null;
  worktree_path?: string | null;
  pr_url?: string | null;
  created_at?: string | null;
  approval_gate?: string | null;
  pending_question?: string | null;
  conversation_id?: number | null;
  trace_id?: string | null;
  event_seq?: number | null;
  protocol_version?: string | null;
  logs?: Array<{ message?: string; type?: string; ts?: string } | string> | null;
}

interface Props {
  ticketKey?: string;
  compact?: boolean;
  onOpenConversation?: (ticketKey: string) => void;
}

const statusColor = (status: string) => {
  if (status === 'running') return 'text-blue-600';
  if (status === 'pending' || status === 'queued') return 'text-slate-600';
  if (status === 'waiting_user') return 'text-amber-600';
  if (status === 'success') return 'text-emerald-600';
  if (status === 'failed') return 'text-red-600';
  if (status === 'cancelled') return 'text-amber-600';
  return 'text-gray-500';
};

const formatLogLine = (ev: { message?: string; type?: string; ts?: string } | string) => {
  if (typeof ev === 'string') return ev;
  return ev.message || ev.type || '';
};

const AgentRunsPanel: React.FC<Props> = ({ ticketKey, compact, onOpenConversation }) => {
  const { currentProject } = useProject();
  const [agents, setAgents] = useState<AgentSpec[]>([]);
  const [runs, setRuns] = useState<AgentRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [session, setSession] = useState<AgentRun | null>(null);
  const [liveLines, setLiveLines] = useState<string[]>([]);
  const [agentCommands, setAgentCommands] = useState<AgentCommandBlock[]>([]);
  const [commandExpanded, setCommandExpanded] = useState<Record<string, boolean>>({});
  const [answer, setAnswer] = useState('');
  const [sending, setSending] = useState(false);
  const logEndRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const [a, r] = await Promise.all([
        axios.get(`${API_URL}/api/agents`),
        axios.get(`${API_URL}/api/agents/runs`, {
          params: {
            project_id: currentProject?.id,
            ticket_key: ticketKey || undefined,
            limit: compact ? 10 : 40,
          },
        }),
      ]);
      setAgents(a.data.agents || []);
      setRuns(r.data.runs || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [currentProject?.id, ticketKey, compact]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
  }, [refresh]);

  // Auto-select newest active run
  useEffect(() => {
    if (selectedId) return;
    const active = runs.find((r) =>
      ['running', 'pending', 'queued', 'waiting_user'].includes(r.status)
    );
    if (active) setSelectedId(active.run_id);
  }, [runs, selectedId]);

  const loadSession = useCallback(async (runId: string) => {
    try {
      const res = await axios.get(`${API_URL}/api/agents/runs/${runId}`);
      const run: AgentRun = res.data.run || res.data;
      setSession(run);
      const hist = (run.logs || [])
        .map(formatLogLine)
        .filter(Boolean)
        .slice(-80);
      setLiveLines(hist);
      const cmds = commandsFromAgentLogs(run.logs as any[]);
      setAgentCommands(cmds);
      const expanded: Record<string, boolean> = {};
      cmds.forEach((c) => {
        expanded[c.id] = c.status === 'running';
      });
      setCommandExpanded(expanded);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setSession(null);
      setLiveLines([]);
      setAgentCommands([]);
      return;
    }
    loadSession(selectedId);
    const ws = new WebSocket(`${getWsBaseUrl()}/ws/agent-${selectedId}`);
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === 'agent_event' && data.event) {
          const e = data.event;
          if (e.type === 'command') {
            setAgentCommands((prev) => applyCommandEvent(prev, e));
            if (e.state === 'start') {
              setLiveLines((prev) => [...prev.slice(-120), `$ ${e.payload?.command || e.message}`]);
            } else if (e.state === 'running') {
              const line = e.payload?.line || e.message;
              if (line) setLiveLines((prev) => [...prev.slice(-120), line]);
            } else if (e.state === 'end') {
              setLiveLines((prev) => [
                ...prev.slice(-120),
                `↳ exit ${e.payload?.returncode ?? '?'}`,
              ]);
            }
          } else if (e.message) {
            setLiveLines((prev) => [...prev.slice(-120), e.message]);
          }
        }
        if (data.type === 'agent_status') {
          loadSession(selectedId);
          refresh();
        }
      } catch {
        /* ignore */
      }
    };
    const ping = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send('ping');
    }, 25000);
    return () => {
      clearInterval(ping);
      ws.close();
    };
  }, [selectedId, loadSession, refresh]);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [liveLines]);

  const startDeveloper = async (mode: string) => {
    if (!currentProject?.id) return;
    if (!ticketKey && mode !== 'list') {
      alert('Vyber tiket');
      return;
    }
    try {
      setStarting(true);
      const res = await axios.post(`${API_URL}/api/agents/start`, {
        agent_id: 'developer',
        ticket_key: ticketKey,
        project_id: currentProject.id,
        mode,
        goal: `Developer ${mode} on ${ticketKey}`,
        open_conversation: true,
      });
      if (ticketKey && onOpenConversation) onOpenConversation(ticketKey);
      if (res.data.run_id) setSelectedId(res.data.run_id);
      if (res.data.queued) {
        alert(
          `Zařazeno do fronty (pozice ${res.data.position}). Spustí se po dokončení běžícího agenta.`
        );
      }
      await refresh();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to start agent');
    } finally {
      setStarting(false);
    }
  };

  const cancelRun = async (runId: string) => {
    try {
      setAgentCommands((prev) =>
        prev.map((c) =>
          c.status === 'running'
            ? {
                ...c,
                status: 'error' as const,
                returncode: 130,
                output: [...c.output, '[complic] stopped by user'],
              }
            : c
        )
      );
      await axios.post(`${API_URL}/api/agents/runs/${runId}/cancel`);
      await refresh();
      if (selectedId === runId) loadSession(runId);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Cancel failed');
    }
  };

  const approveRun = async (runId: string, text?: string) => {
    try {
      setSending(true);
      await axios.post(`${API_URL}/api/agents/runs/${runId}/approve`, {
        ...(text?.trim() ? { answer: text.trim() } : {}),
      });
      setAnswer('');
      await refresh();
      loadSession(runId);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Approve failed');
    } finally {
      setSending(false);
    }
  };

  const rejectRun = async (runId: string) => {
    try {
      await axios.post(`${API_URL}/api/agents/runs/${runId}/reject`, {
        reason: 'Rejected from UI',
      });
      await refresh();
      if (selectedId === runId) loadSession(runId);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Reject failed');
    }
  };

  const selected = session || runs.find((r) => r.run_id === selectedId) || null;
  const question = selected?.pending_question;

  return (
    <div className={compact ? '' : 'p-4'}>
      {!compact && (
        <div className="mb-4">
          <h2 className="text-xl font-sans font-bold text-gray-900 dark:text-white">Agents</h2>
          <p className="text-sm text-gray-500 font-sans">
            Live session — sleduj progress a odpovídej, když se agent doptá.
          </p>
        </div>
      )}

      <div className={`flex flex-wrap ${compact ? 'gap-1 mb-2' : 'gap-2 mb-3'}`}>
        {(compact
          ? (['plan', 'full'] as const)
          : agents.find((a) => a.id === 'developer')?.modes || ['full', 'plan', 'implement', 'push']
        ).map((mode) => (
            <button
              key={mode}
              disabled={starting || !ticketKey}
              onClick={() => startDeveloper(mode)}
              className={`${
                compact ? 'px-2 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'
              } font-sans rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white`}
            >
              {compact ? `Dev ${mode}` : `Developer · ${mode}`}
            </button>
          ))}
      </div>

      <div className={`grid gap-3 ${compact ? 'grid-cols-1' : 'lg:grid-cols-2'}`}>
        <div>
          {loading && runs.length === 0 ? (
            <p className="text-xs text-gray-500">Načítám…</p>
          ) : runs.length === 0 ? (
            <p className="text-xs text-gray-500">
              Zatím žádné běhy{ticketKey ? ` pro ${ticketKey}` : ''}.
            </p>
          ) : (
            <ul className={`overflow-y-auto ${compact ? 'space-y-1 max-h-64' : 'space-y-2 max-h-[28rem]'}`}>
              {runs.map((r) => (
                <li key={r.run_id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(r.run_id)}
                    className={`w-full text-left transition-colors ${
                      compact
                        ? `rounded px-2 py-1.5 ${
                            selectedId === r.run_id
                              ? 'bg-blue-50 dark:bg-blue-950/40'
                              : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'
                          }`
                        : `border rounded-lg px-3 py-2 bg-white dark:bg-gray-800 ${
                            selectedId === r.run_id
                              ? 'border-blue-500 ring-1 ring-blue-400/40'
                              : 'border-gray-200 dark:border-gray-700'
                          }`
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className={`${compact ? 'text-xs' : 'text-sm'} font-sans font-semibold text-gray-900 dark:text-white truncate`}>
                          {r.agent_id === 'developer' ? 'dev' : r.agent_id}
                          {r.ticket_key && !compact ? ` · ${r.ticket_key}` : ''}
                          <span className="font-normal text-gray-500"> · {r.mode}</span>
                        </p>
                        <p className={`text-xs font-sans ${statusColor(r.status)}`}>
                          {r.status}
                          {r.status_detail ? ` — ${r.status_detail}` : ''}
                        </p>
                        {r.pending_question && (
                          <p className="text-xs text-amber-700 dark:text-amber-300 mt-1 line-clamp-2">
                            ❓ {r.pending_question}
                          </p>
                        )}
                        {r.progress && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {Object.entries(r.progress).map(([k, v]) => (
                              <span
                                key={k}
                                className={`text-[10px] px-1.5 py-0.5 rounded ${
                                  v === 'done'
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : v === 'running'
                                      ? 'bg-blue-100 text-blue-800'
                                      : v === 'failed'
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-gray-100 text-gray-500'
                                }`}
                              >
                                {k}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                        {r.branch_name && (
                          <button
                            type="button"
                            onClick={() => openAgentVscode(r.run_id)}
                            className="text-xs px-2 py-1 rounded bg-indigo-100 text-indigo-800 hover:bg-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-200"
                            title={`VS Code · ${r.branch_name}`}
                          >
                            VS Code
                          </button>
                        )}
                        {r.conversation_id && (
                          <button
                            type="button"
                            onClick={() => {
                              window.dispatchEvent(
                                new CustomEvent('complic-open-conversation', {
                                  detail: {
                                    conversationId: r.conversation_id,
                                    ticketKey: r.ticket_key,
                                  },
                                })
                              );
                              if (onOpenConversation && r.ticket_key) {
                                onOpenConversation(r.ticket_key);
                              }
                            }}
                            className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800 hover:bg-blue-200"
                          >
                            Chat
                          </button>
                        )}
                        {r.status === 'waiting_user' && !r.pending_question && (
                          <>
                            <button
                              type="button"
                              onClick={() => approveRun(r.run_id)}
                              className="text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              onClick={() => rejectRun(r.run_id)}
                              className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-800 hover:bg-amber-200"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {['running', 'pending', 'queued', 'waiting_user'].includes(r.status) && (
                          <button
                            type="button"
                            onClick={() => cancelRun(r.run_id)}
                            className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200"
                          >
                            {r.status === 'queued' ? 'Odebrat' : 'Stop'}
                          </button>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {selected && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/60 flex flex-col min-h-[16rem] max-h-[28rem]">
            <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs font-mono text-gray-500 truncate">
                  session {selected.run_id.slice(0, 8)}
                  {selected.trace_id ? ` · trace ${selected.trace_id.slice(0, 8)}` : ''}
                </p>
                <p className={`text-xs ${statusColor(selected.status)}`}>
                  {selected.status}
                  {selected.status_detail ? ` — ${selected.status_detail}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {selected.conversation_id && (
                  <button
                    type="button"
                    onClick={() => {
                      window.dispatchEvent(
                        new CustomEvent('complic-open-conversation', {
                          detail: {
                            conversationId: selected.conversation_id,
                            ticketKey: selected.ticket_key,
                          },
                        })
                      );
                      if (onOpenConversation && selected.ticket_key) {
                        onOpenConversation(selected.ticket_key);
                      }
                    }}
                    className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800 hover:bg-blue-200"
                  >
                    Chat
                  </button>
                )}
                {selected.branch_name && (
                  <button
                    type="button"
                    onClick={() => openAgentVscode(selected.run_id)}
                    className="text-xs px-2 py-1 rounded bg-indigo-100 text-indigo-800 hover:bg-indigo-200"
                    title={selected.branch_name}
                  >
                    VS Code
                  </button>
                )}
                {selected.pr_url && (
                  <a
                    href={selected.pr_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-blue-600 underline"
                  >
                    PR
                  </a>
                )}
                {['running', 'pending', 'queued', 'waiting_user'].includes(selected.status) && (
                  <button
                    type="button"
                    onClick={() => cancelRun(selected.run_id)}
                    className="text-xs px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                    title="Zastavit agent run"
                  >
                    Stop
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto flex flex-col min-h-0">
              {agentCommands.length > 0 && (
                <div className="px-3 py-2 space-y-2 border-b border-gray-200 dark:border-gray-700 max-h-56 overflow-y-auto">
                  {agentCommands.map((cmd) => (
                    <AgentCommandCard
                      key={cmd.id}
                      command={cmd}
                      expanded={commandExpanded[cmd.id] ?? cmd.status === 'running'}
                      onToggle={() => {
                        const currently = commandExpanded[cmd.id] ?? cmd.status === 'running';
                        setCommandExpanded((prev) => ({ ...prev, [cmd.id]: !currently }));
                      }}
                      onStop={
                        selected &&
                        ['running', 'pending', 'waiting_user'].includes(selected.status)
                          ? () => cancelRun(selected.run_id)
                          : undefined
                      }
                    />
                  ))}
                </div>
              )}
              <div className="flex-1 overflow-y-auto px-3 py-2 font-mono text-[11px] leading-relaxed text-gray-700 dark:text-gray-300 space-y-0.5">
                {liveLines.length === 0 ? (
                  <p className="text-gray-400">Čekám na logy…</p>
                ) : (
                  liveLines
                    .filter((line) => !line.startsWith('$ ') && !line.startsWith('↳ exit'))
                    .slice(-60)
                    .map((line, i) => (
                      <div key={`${i}-${line.slice(0, 24)}`} className="break-words">
                        {line}
                      </div>
                    ))
                )}
                <div ref={logEndRef} />
              </div>
            </div>

            {selected.status === 'waiting_user' && (
              <div className="px-3 py-2 border-t border-amber-300/50 bg-amber-50/80 dark:bg-amber-950/40 space-y-2">
                {question ? (
                  <>
                    <p className="text-xs text-amber-900 dark:text-amber-100">{question}</p>
                    <div className="flex gap-2">
                      <input
                        value={answer}
                        onChange={(e) => setAnswer(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && answer.trim()) {
                            approveRun(selected.run_id, answer);
                          }
                        }}
                        placeholder="Odpověď agenta…"
                        className="flex-1 text-xs px-2 py-1.5 rounded border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                      />
                      <button
                        type="button"
                        disabled={sending || !answer.trim()}
                        onClick={() => approveRun(selected.run_id, answer)}
                        className="text-xs px-3 py-1.5 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40"
                      >
                        Odeslat
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={sending}
                      onClick={() => approveRun(selected.run_id)}
                      className="text-xs px-3 py-1.5 rounded bg-emerald-600 text-white"
                    >
                      Schválit
                    </button>
                    <button
                      type="button"
                      onClick={() => rejectRun(selected.run_id)}
                      className="text-xs px-3 py-1.5 rounded bg-amber-100 text-amber-900"
                    >
                      Odmítnout
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentRunsPanel;
